import type { EndorseTarget, MetaResponse, SubmissionRow } from '../api/client';

/**
 * The participant board, driven by the API's status ladder.
 *
 * This file used to hold a five-column pipeline invented for the design mock —
 * Intake/Referral → Assessment → Monitoring, plus a waiting list and a screen-fail
 * bucket — and a set of rules for projecting each form's own statuses onto it.
 * None of that was real. 07 §2 defines one ladder for every kind of inquiry and
 * 02 gives the staff model behind it, so the columns are now the statuses
 * themselves and the projection is gone.
 */

export type MetaStatus = MetaResponse['statuses'][number];

export interface Column {
  key: string;
  label: string;
  blurb: string;
  group: MetaStatus['group'];
  /** Statuses that land in this column — one each, except the closed bucket. */
  statuses: string[];
}

/** Colour per group. Presentation, so it lives here rather than on the API. */
export const groupDot: Record<MetaStatus['group'], string> = {
  open: '#33628e',
  parked: '#e8a256',
  closed: '#9aa8b6',
};

/**
 * One column per funnel step, then the parked statuses, then a single column
 * for everything that has finished.
 *
 * Terminal states are grouped because a board with a column each for Completed,
 * Not a match, Stopped and Done is four columns of mostly nothing; what a
 * coordinator needs from them is "this person is no longer live work".
 */
export function columnsFrom(meta: MetaResponse): Column[] {
  const byKey = new Map(meta.statuses.map((s) => [s.key, s]));
  const funnel = meta.statuses
    .filter((s) => s.funnelIndex >= 0 && s.group !== 'closed')
    .sort((a, b) => a.funnelIndex - b.funnelIndex)
    .map((s) => ({
      key: s.key,
      label: s.label,
      blurb: s.blurb,
      group: s.group,
      statuses: [s.key],
    }));

  const parked = meta.statuses
    .filter((s) => s.group === 'parked')
    .map((s) => ({
      key: s.key,
      label: s.label,
      blurb: s.blurb,
      group: s.group,
      statuses: [s.key],
    }));

  const closedKeys = meta.statuses.filter((s) => s.group === 'closed').map((s) => s.key);
  const closed: Column[] = closedKeys.length
    ? [
        {
          key: 'closed',
          label: 'Closed',
          blurb: closedKeys.map((k) => byKey.get(k)?.label ?? k).join(' · '),
          group: 'closed' as const,
          statuses: closedKeys,
        },
      ]
    : [];

  return [...funnel, ...parked, ...closed];
}

/**
 * Where an endorsement from another study lands someone (API migration 035).
 *
 * Not a funnel step and not a column `columnsFrom` builds: it is the other
 * way into a study, beside Forms submission, and a board draws it as its own
 * first column. Only `POST /submissions/{id}/endorse` sets it — the API
 * refuses it on the status route — so no status control here offers it.
 * See docs/api-contract-endorsements.md.
 */
export const ENDORSED = 'endorsed';

/** Whether the API records endorsements. One older than 035 has no such status. */
export function endorsable(meta: MetaResponse): boolean {
  return meta.statuses.some((s) => s.key === ENDORSED);
}

/**
 * Every study a row may be endorsed to: the whole registry, not only the
 * caller's own, minus the one the row is in. Handing someone to a team you
 * do not work in is the usual case. Empty against an API older than 035,
 * which sends no list.
 */
export function endorseTargetsFor(meta: MetaResponse, row: SubmissionRow): EndorseTarget[] {
  const here = studySlugOf(row);
  return (meta.endorseTargets ?? []).filter((s) => s.key !== here);
}

/**
 * The three sentences a hand-off is explained with — the question, what it
 * does, and what was done — in one place, so the record, the inbox and the
 * board all ask and answer in the same words.
 */
export function endorseCopy(
  meta: MetaResponse,
  name: string,
  // A `/meta` target, or the `to` the endorse route answers with.
  target: Pick<EndorseTarget, 'key' | 'short'>,
): { title: string; detail: string; done: string } {
  const endorsed = meta.statuses.find((s) => s.key === ENDORSED);
  const column = endorsed?.label ?? 'Newly endorsed';
  // `studies` is the caller's own; a target outside it takes the record out
  // of their reach, which the question has to say before they commit.
  const staysReachable = meta.studies.some((s) => s.key === target.key);
  const lands = `${target.short}'s ${column} column until its team accepts them`;
  return {
    title: `Endorse ${name} to ${target.short}?`,
    detail:
      `They move to ${lands}` +
      (endorsed?.blurb ? `, and will be shown: “${endorsed.blurb}”` : '.') +
      (staysReachable
        ? ''
        : ` You do not work on ${target.short}, so you will no longer be able to open this record.`),
    done: `Endorsed ${name} to ${target.short}. They are in ${lands}.`,
  };
}

/**
 * The status that accepts someone into a study: the first column of its
 * board, whatever the API says that is. The inbox's Accept and the Newly
 * endorsed column's both move people here.
 */
export function acceptStatus(meta: MetaResponse): MetaStatus | null {
  const first = boardColumns(meta)[0];
  return first ? (meta.statuses.find((s) => s.key === first.statuses[0]) ?? null) : null;
}

/**
 * The next step down the funnel, or null at the end of it.
 *
 * Only moves forward along the funnel; parked and closed states are chosen
 * explicitly, never stumbled into by pressing "next". Someone newly endorsed
 * has not been accepted yet, so their next step is the accepting one.
 */
export function advanceStatus(meta: MetaResponse, row: SubmissionRow): MetaStatus | null {
  if (row.status === ENDORSED) return acceptStatus(meta);
  const here = meta.statuses.find((s) => s.key === row.status);
  if (!here || here.funnelIndex < 0) return null;
  return (
    meta.statuses
      .filter((s) => s.funnelIndex === here.funnelIndex + 1)
      .find(() => true) ?? null
  );
}

/**
 * The four participant pathways. Everything they collect lands in Forms
 * submission first; a study's board only shows what was accepted from there.
 * Inquiries and requests are handled separately, under Messages.
 */
export const PARTICIPANT_FORMS = ['intake', 'referral', 'waitlist', 'early-interest'] as const;

/**
 * Where a form sits before anyone has accepted it into a study.
 *
 * `received` is the API's starting status; `waitlist` and `early-interest`
 * are parked on the lab rather than on the person. None of the three is a
 * participant yet, so all three live on the Forms submission inbox rather
 * than on a study's board.
 */
export const INBOX_STATUSES: readonly string[] = ['received', 'waitlist', 'early-interest'];

export const isInbox = (row: SubmissionRow): boolean => INBOX_STATUSES.includes(row.status);

/**
 * One study's board: the funnel from Intake scheduled on, then Hold, then the
 * closed bucket. Received, Waitlist and Early interest are not columns here
 * because nobody in them has been accepted into the study yet — that is what
 * the inbox is for, and what the first column's status means.
 */
export function boardColumns(meta: MetaResponse): Column[] {
  return columnsFrom(meta).filter(
    (c) => c.group === 'closed' || (c.key !== 'received' && !INBOX_STATUSES.includes(c.key)),
  );
}

/**
 * The study a row belongs to: the API's column when it has one, else the
 * first the payload names. The column is what the API scopes and filters on,
 * so it wins; the payload only covers an older API that has not set it.
 */
export function studySlugOf(row: SubmissionRow): string | null {
  const column = (row as { studySlug?: string | null }).studySlug;
  if (typeof column === 'string' && column) return column;
  return studyKeysOf(row)[0] ?? null;
}

/** The study a participant row is attached to, as a `/meta` study key. */
export function studyKeyOf(row: SubmissionRow): string | null {
  return studySlugOf(row);
}

/**
 * Every study a row names. One for the intake, referral and early-interest
 * forms; a waitlist sign-up may tick several. The inbox filters on any of
 * them; a board shows the row when it is one of them.
 */
export function studyKeysOf(row: SubmissionRow): string[] {
  const d = row.data as Record<string, unknown>;
  if (row.form === 'waitlist') {
    const studies = d.studies;
    return Array.isArray(studies) ? studies.filter((s): s is string => typeof s === 'string') : [];
  }
  const study = d.study;
  return typeof study === 'string' && study ? [study] : [];
}

/**
 * Statuses a coordinator can move someone to that are not the next funnel step.
 *
 * For someone on the funnel that is the parked and closed states. Someone who
 * is parked or closed has no "next" at all, so the funnel steps are offered
 * here too — otherwise a waitlist signup could never be brought in when a place
 * opens, and a mistaken "Not a match" could never be undone.
 */
export function sideStatuses(meta: MetaResponse, row: SubmissionRow): MetaStatus[] {
  const here = meta.statuses.find((s) => s.key === row.status);
  const offFunnel = meta.statuses.filter((s) => s.group !== 'open');
  if (!here || here.group === 'open') return offFunnel.filter((s) => s.key !== row.status);
  const funnel = meta.statuses
    .filter((s) => s.group === 'open' && s.key !== ENDORSED)
    .sort((a, b) => a.funnelIndex - b.funnelIndex);
  return [...funnel, ...offFunnel].filter((s) => s.key !== row.status);
}

/**
 * What a plain status select may offer for a row: the whole ladder but
 * `endorsed`, which comes only with a hand-off — unless the row is in it, so
 * the select still shows where it is.
 */
export function statusOptions(statuses: readonly MetaStatus[], current: string): MetaStatus[] {
  return statuses.filter((s) => s.key !== ENDORSED || s.key === current);
}

/** The participant-safe label for a stored status key. */
export function statusLabelOf(meta: MetaResponse, status: string | null | undefined): string {
  if (!status) return '—';
  return meta.statuses.find((s) => s.key === status)?.label ?? status;
}
